// Pure logic for the cascading category→attribute filter in the SKU picker
// (SkuSearchSelect). React-free + node-testable (same convention as sku-search.ts).
//
// CRITICAL — mirror-safe attribute values: the *Color/*Material/*Size fields are
// DUPLICATED across category prefixes (coilColor === downspoutColor === … for any
// row), so a generic prefix match is wrong. We resolve every attribute value through
// `unifiedFilterAccessor`, which maps a label to the CANONICAL field via the SKU's
// OWN category columns (catalogColumnsForCategory). So this file never names a
// concrete attribute field — it only ever uses (categoryName, label).

import type { InventorySkuRow } from '@gutterinv/shared';
import { catalogColumnsForCategory, unifiedFilterAccessor } from '../pages/inventory-table-config.js';
import { distinctValues, facetValue, BLANK } from './column-filter.js';

/** Display-order overrides for a category's attribute filters (a DISPLAY concern,
 *  NOT the field mapping — fields always come from catalogColumnsForCategory).
 *  Categories absent here fall back to cardinality-ascending (low→high, so the
 *  highest-cardinality attribute like Color lands last) with the config column
 *  order as a stable tiebreak. */
const CATEGORY_FILTER_ORDER: Record<string, string[]> = {
  'Gutter Coil': ['Profile', 'Temper', 'Material', 'Thickness', 'Size', 'Width', 'Color'],
};

/** Non-blank distinct facet values for one attribute label among `rows`. */
export function distinctAttrValues(rows: InventorySkuRow[], label: string): string[] {
  return distinctValues(rows, (s) => unifiedFilterAccessor(s, label)).filter((v) => v !== BLANK);
}

/** Apply the category + attribute filters (free-text search is applied separately
 *  by filterSkus). Each attribute is single-select. */
export function applyPickerFilters(
  skus: InventorySkuRow[],
  categoryId: string | null,
  attrFilters: Record<string, string>,
): InventorySkuRow[] {
  let out = categoryId ? skus.filter((s) => s.categoryId === categoryId) : skus;
  for (const [label, value] of Object.entries(attrFilters)) {
    if (!value) continue;
    out = out.filter((s) => facetValue(unifiedFilterAccessor(s, label)) === value);
  }
  return out;
}

/** The ordered attribute-filter labels to SHOW for a category: its catalog columns
 *  that have >1 distinct (non-blank) value among the category's SKUs (skip useless
 *  single-value/empty filters). Order = explicit override if any (filtered to the
 *  shown set, then any extras by cardinality), else cardinality-ascending + config
 *  order tiebreak. Catch-all categories (no catalog columns) → []. */
export function attributeFiltersForCategory(
  categoryName: string,
  categorySkus: InventorySkuRow[],
): string[] {
  const cols = catalogColumnsForCategory(categoryName);
  const shown = cols
    .map((c, idx) => ({ label: c.label, idx, n: distinctAttrValues(categorySkus, c.label).length }))
    .filter((c) => c.n > 1);

  const byCardinality = [...shown].sort((a, b) => a.n - b.n || a.idx - b.idx).map((c) => c.label);

  const override = CATEGORY_FILTER_ORDER[categoryName.trim()];
  if (!override) return byCardinality;
  const shownSet = new Set(byCardinality);
  const ordered = override.filter((l) => shownSet.has(l));
  // Append any shown filter not named in the override (cardinality order).
  for (const l of byCardinality) if (!ordered.includes(l)) ordered.push(l);
  return ordered;
}

/** Dependent options for one filter: the distinct values present among the SKUs
 *  surviving the category + every OTHER active attribute filter — so no pick ever
 *  leads to an empty result. */
export function optionsForFilter(
  skus: InventorySkuRow[],
  categoryId: string | null,
  attrFilters: Record<string, string>,
  label: string,
): string[] {
  const others = { ...attrFilters };
  delete others[label];
  return distinctAttrValues(applyPickerFilters(skus, categoryId, others), label);
}
